import { StanAccordion, StanAccordionBody, StanAccordionHeader, StanAccordionItem } from '@/components';
import { createIconElement } from '@/lib/utils/createIconElement';
import type { Booking, BookingAttendee } from '../../types/appointment.types';

import UserIconSvg from '@/assets/icons/ic-user-01.svg';

const UserIcon = createIconElement(UserIconSvg);

interface AttendeesInfoProps {
    booking: Booking;
}

export function AttendeesInfo({ booking }: AttendeesInfoProps) {
    const attendees = booking.attendees || [];
    const attendeesCount = attendees.length;
    const attendeesLimit = booking.attendees_limit || 0;
    const remainingSeats = attendeesLimit - attendeesCount;

    const getSeatsLeft = () => {
        if (remainingSeats === 0) {
            return '(No seats left)';
        }
        if (remainingSeats === 1) {
            return '(1 seat left)';
        }
        return `(${remainingSeats} seats left)`;
    };

    return (
        <div>
            <div className='mb-6'>
                <h2 className='text-dark heading-heading2 m-0'>Attendees Info</h2>
            </div>

            <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-1'>
                    <label className='text-gray text-body-sm font-medium'>Attendees</label>
                    <span className='text-dark text-base'>
                        {attendeesCount} of {attendeesLimit} attendees {getSeatsLeft()}
                    </span>
                </div>

                {attendees.length > 0 && (
                    <StanAccordion className='flex flex-col gap-3'>
                        {attendees.map((attendee: BookingAttendee) => (
                            <StanAccordionItem
                                key={`attendee-${attendee.email}-${attendee.name}`}
                                id={`attendee-${attendee.email}`}
                                isCard
                            >
                                <StanAccordionHeader>
                                    <div className='flex items-center gap-2'>
                                        {UserIcon}
                                        <span className='text-dark text-base font-semibold'>{attendee.name}</span>
                                    </div>
                                </StanAccordionHeader>
                                <StanAccordionBody>
                                    <div className='flex flex-col gap-2 pt-4'>
                                        <div className='flex flex-col gap-1'>
                                            <label className='text-gray text-body-sm font-medium'>Email</label>
                                            <span className='text-dark text-body-sm'>{attendee.email}</span>
                                        </div>
                                        {attendee.status && (
                                            <div className='flex flex-col gap-1'>
                                                <label className='text-gray text-body-sm font-medium'>Status</label>
                                                <span className='text-dark text-body-sm'>{attendee.status}</span>
                                            </div>
                                        )}
                                        {attendee.additional_notes && attendee.additional_notes.length > 0 && (
                                            <div className='flex flex-col gap-1'>
                                                <label className='text-gray text-body-sm font-medium'>
                                                    Additional Information
                                                </label>
                                                <div className='flex flex-col gap-2'>
                                                    {attendee.additional_notes.map(
                                                        (note: { question?: string; answer?: string } | string) => {
                                                            // Generate unique key based on content
                                                            const noteKey =
                                                                typeof note === 'object' && note !== null
                                                                    ? `${note.question}-${note.answer}`
                                                                    : `${note}`;

                                                            return (
                                                                <div
                                                                    key={`note-${attendee.email}-${noteKey}`}
                                                                    className='bg-neutral-subtle rounded p-3'
                                                                >
                                                                    {typeof note === 'object' && note !== null ? (
                                                                        <>
                                                                            <div className='text-gray text-body-xs mb-1 font-medium'>
                                                                                {note.question}
                                                                            </div>
                                                                            <div className='text-dark text-body-sm'>
                                                                                {note.answer}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <span className='text-dark text-body-sm'>
                                                                            {note}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </StanAccordionBody>
                            </StanAccordionItem>
                        ))}
                    </StanAccordion>
                )}
            </div>
        </div>
    );
}
